import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import classNames from "classnames";
import Linkify from "linkify-react";
import { Typography } from "@mui/material";

import ConfirmDialog from "../ConfirmDialog";
import styles from "./linkifyContainer.module.scss";

export interface LinkifyContainerProps {
  alreadySeenLinks: string[];
  children: React.ReactNode;
  confirmNavigation: boolean;
  onConfirmNavigation: (clickedLink: string | null) => void;
}

function LinkifyContainer(props: LinkifyContainerProps) {
  const { alreadySeenLinks, children, confirmNavigation, onConfirmNavigation } = props;
  const [clickedLink, setClickedLink] = useState<null | string>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  /**
   * Handle open dialog
   */
  const handleOpenDialog = () => {
    setDialogOpen(true);

    navigate(location.pathname);
  };

  const handleLinkClick = async (e: React.MouseEvent<HTMLLinkElement, MouseEvent>) => {
    const target = e.target as HTMLLinkElement;

    if (confirmNavigation && !alreadySeenLinks.includes(target.href)) {
      e.preventDefault();
      handleOpenDialog();
    }
    setClickedLink(target.href);
  };

  /**
   * Handle close dialog
   */
  const handleCloseDialog = () => {
    setDialogOpen(false);
    navigate(-1);
  };

  const handleConfirm = () => {
    onConfirmNavigation(clickedLink);
    handleCloseDialog();
  };

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setDialogOpen(false);
    }
  }, [navigationType]);

  return (
    <>
      <Linkify
        options={{
          target: "_blank",
          rel: "noopener noreferrer",
          protocol: "https",
          format: (value: string, type: string) => {
            if (type === "url" && value.length > 40) {
              return `${value.slice(0, 40)}...`;
            }
            return value;
          },
          attributes: {
            className: classNames(styles.link, styles.breakAll),
            onClick: handleLinkClick,
          },
        }}
      >
        {children}
      </Linkify>
      <ConfirmDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        title='Be careful!'
        body={
          <Typography>
            Links can be harmful for your device. Do you still want to open
            <b className={styles.breakAll}> {clickedLink}</b> ? (to{" "}
            <Typography component='span' color='warning.main'>
              disable this popup
            </Typography>{" "}
            for further links go to account settings)
          </Typography>
        }
        closeLabel='Cancel'
        confirmLabel='Open'
        onConfirm={handleConfirm}
        shouldSetLoading={false}
      />
    </>
  );
}

export default LinkifyContainer;
